import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, Modal, Form, message, Select, Empty, Input, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { getAllDevices, addDevice, updateDevice, deleteDevice, batchDeleteDevices, getAllRooms, downloadDeviceTemplate, batchImportDevices } from '../../api';
import ListToolbar from '../../components/ListToolbar';
import {
  DEVICE_LIST_SEARCH_SCOPE_OPTIONS,
  filterDeviceList,
  filterDeviceListByLocation,
  getDeviceListSearchPlaceholder,
  getDeviceLocationSearchPlaceholder,
} from '../../utils/listSearch';
import { exportToCSV } from '../../utils/exportUtils';
import PageTitle from '../../components/PageTitle';
import ResizableTable from '../../components/ResizableTable';
import { isDevice4GMeter, isZtManufacturer, isYjWaterRemoteSupplementEligible } from '../../utils/deviceMeter';
import MeterSupplementModal from '../../components/MeterSupplementModal';
import MeterMeteringFactorModal from '../../components/MeterMeteringFactorModal';
import MeterMeteringTotalModal from '../../components/MeterMeteringTotalModal';
import { showBatchImportResult, showBatchImportHttpError } from '../../utils/batchImportModal';

const { Option } = Select;
const { Text } = Typography;
const DEVICE_STATUS_OPTIONS = [
  { value: '1', label: '在线/正常' },
  { value: '0', label: '离线/故障' },
];
const DEVICE_TYPE_OPTIONS = [
  { value: 1, label: '电表' },
  { value: 2, label: '水表' },
  { value: 3, label: '门锁' },
];
// ✅ 固化类型：水表=2
const CURRENT_DEVICE_TYPE = { value: 2, label: '水表' };

const WaterMeterList = () => {
  const [devices, setDevices] = useState([]);
  const [allDevices, setAllDevices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [searchScope, setSearchScope] = useState('all');
  const [matchMode, setMatchMode] = useState('fuzzy');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [appliedSearchScope, setAppliedSearchScope] = useState('all');
  const [appliedMatchMode, setAppliedMatchMode] = useState('fuzzy');
  const [locationText, setLocationText] = useState('');
  const [locationScope, setLocationScope] = useState('all');
  const [locationMatchMode, setLocationMatchMode] = useState('fuzzy');
  const [appliedLocationKeyword, setAppliedLocationKeyword] = useState('');
  const [appliedLocationScope, setAppliedLocationScope] = useState('all');
  const [appliedLocationMatchMode, setAppliedLocationMatchMode] = useState('fuzzy');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingDevice, setEditingDevice] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [supplementDevice, setSupplementDevice] = useState(null);
  const [meteringFactorDevice, setMeteringFactorDevice] = useState(null);
  const [meteringTotalDevice, setMeteringTotalDevice] = useState(null);
  const [platformSupplementDevice, setPlatformSupplementDevice] = useState(null);
  const [waterMeterNeedReadIds, setWaterMeterNeedReadIds] = useState(() => new Set());
  const watchedCommunicationType = Form.useWatch('communication_type', form);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const loadRooms = async () => {
    try {
      const response = await getAllRooms();
      const roomList = response?.success && Array.isArray(response.data) ? response.data : [];
      setRooms(roomList);
    } catch (error) {
      console.error('加载房间失败：', error);
      message.error('加载房间列表失败');
    }
  };

  // ✅ 自动过滤【水表】类型
  const loadDevices = async () => {
    try {
      setLoading(true);
      const response = await getAllDevices(CURRENT_DEVICE_TYPE.value);
      const deviceList = response?.success && Array.isArray(response.data) 
        ? response.data.filter(item => item.device_type === CURRENT_DEVICE_TYPE.value) 
        : [];
      setDevices(deviceList);
      setAllDevices(deviceList);
      message.success(`加载成功 ${deviceList.length} 个${CURRENT_DEVICE_TYPE.label}`);
    } catch (error) {
      console.error('加载设备失败：', error);
      const errorMsg = error.response?.data?.error || `加载${CURRENT_DEVICE_TYPE.label}列表失败，请稍后重试`;
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRooms();
    loadDevices();
  }, []);

  const applyDeviceFilter = (source, keyword, scope, mode, locKeyword, locScope, locMode) => {
    let list = source;
    if (keyword) {
      list = filterDeviceList(list, keyword, scope, mode);
    }
    if (locKeyword) {
      list = filterDeviceListByLocation(list, locKeyword, locScope, locMode);
    }
    return list;
  };

  useEffect(() => {
    const filteredDevices = applyDeviceFilter(
      allDevices,
      appliedKeyword,
      appliedSearchScope,
      appliedMatchMode,
      appliedLocationKeyword,
      appliedLocationScope,
      appliedLocationMatchMode,
    );
    setDevices(filteredDevices);
  }, [
    appliedKeyword,
    appliedSearchScope,
    appliedMatchMode,
    appliedLocationKeyword,
    appliedLocationScope,
    appliedLocationMatchMode,
    allDevices,
  ]);

  useEffect(() => {
    setPagination((prev) => {
      const total = devices.length;
      const maxPage = Math.max(1, Math.ceil(total / prev.pageSize));
      return {
        ...prev,
        total,
        current: Math.min(prev.current, maxPage),
      };
    });
  }, [devices]);

  const handleSearch = () => {
    const kw = searchText.trim();
    const locKw = locationText.trim();
    setAppliedKeyword(kw);
    setAppliedSearchScope(searchScope);
    setAppliedMatchMode(matchMode);
    setAppliedLocationKeyword(locKw);
    setAppliedLocationScope(locationScope);
    setAppliedLocationMatchMode(locationMatchMode);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleMatchModeChange = (mode) => {
    setMatchMode(mode);
    if (!appliedKeyword) return;
    setAppliedMatchMode(mode);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearchScopeChange = (scope) => {
    setSearchScope(scope);
    if (!appliedKeyword) return;
    setAppliedSearchScope(scope);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleLocationMatchModeChange = (mode) => {
    setLocationMatchMode(mode);
    if (!appliedLocationKeyword) return;
    setAppliedLocationMatchMode(mode);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleLocationScopeChange = (scope) => {
    setLocationScope(scope);
    if (!appliedLocationKeyword) return;
    setAppliedLocationScope(scope);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setSearchText('');
    setSearchScope('all');
    setMatchMode('fuzzy');
    setLocationText('');
    setLocationScope('all');
    setLocationMatchMode('fuzzy');
    setAppliedKeyword('');
    setAppliedSearchScope('all');
    setAppliedMatchMode('fuzzy');
    setAppliedLocationKeyword('');
    setAppliedLocationScope('all');
    setAppliedLocationMatchMode('fuzzy');
    setPagination((prev) => ({ ...prev, current: 1 }));
  };
  const handleRefresh = () => loadDevices();
  const handleExport = () => {
    const exportData = devices.map(device => ({
      '水表ID': device.id,
      '设备MAC': device.device_mac || '-',
      '厂商表号': device.factory_meter_no || '-',
      '当前表号': device.current_meter_no || '-',
      'IMEI': device.imei || '-',
      '所属厂家': (() => {
        const code = String(device.manufacturer || '').trim();
        if (code === 'ZT') return '厂家一';
        return code || '-';
      })(),
      '通讯类型': (() => {
        const t = Number(device?.communication_type);
        if (t === 0) return '4G';
        if (t === 1) return '蓝牙';
        if (t === 2) return 'WIFI';
        return '-';
      })(),
      '设备状态': device.device_status_name || '-',
      '最低充值(元)': Number(device.min_recharge || 0).toFixed(2),
      '管理费(元)': Number(device.management_fee || 0).toFixed(2),
      '水表余额(元)': Number(device.electric_balance || 0).toFixed(2),
      '剩余水量(吨)': Number(device.electric_quantity || 0).toFixed(2),
      '总用水量(吨)': Number(device.total_water_quantity ?? device.total_electricity ?? 0).toFixed(2),
      '水价(元/吨)': Number(device.water_price ?? device.electric_price ?? 0).toFixed(2),
      '绑定房间': device.room_name || '-',
      '所属房源': device.house_name || '-',
      '创建时间': device.created_at || '-',
      '更新时间': device.updated_at || '-',
    }));
    if (exportToCSV(exportData, '水表列表')) {
      message.success(`成功导出 ${exportData.length} 条数据`);
    } else {
      message.error('导出数据失败，请重试');
    }
  };

  // 下载批量导入模板
  const handleDownloadTemplate = async () => {
    try {
      await downloadDeviceTemplate();
      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败：', error);
      message.error('下载模板失败，请重试');
    }
  };

  // 批量导入设备
  const fileInputRef = useRef(null);
  const handleBatchImport = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      message.error('请选择CSV文件');
      return;
    }

    try {
      setLoading(true);
      const res = await batchImportDevices(file);
      showBatchImportResult(res, { entityName: '设备' });
      if (res?.success) {
        loadDevices();
      }
    } catch (error) {
      console.error('批量导入失败：', error);
      showBatchImportHttpError(error);
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const showModal = (device = null) => {
    setEditingDevice(device);
    if (device) {
      form.setFieldsValue({
        device_type: device.device_type != null ? Number(device.device_type) : CURRENT_DEVICE_TYPE.value,
        device_mac: device.device_mac,
        factory_meter_no: device.factory_meter_no,
        current_meter_no: device.current_meter_no,
        communication_type: device.communication_type === 0 ? 0 : 1,
        device_detail_type: device.communication_type === 0 ? (device.device_detail_type === 1 ? 1 : 0) : undefined,
        device_status: device.device_status.toString(),
        electric_balance: device.electric_balance || 0,
        electric_quantity: device.electric_quantity || 0,
        min_recharge: device.min_recharge || 0,
        management_fee: device.management_fee || 0,
        bind_room_id: device.bind_room_id ? device.bind_room_id.toString() : '',
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        device_type: CURRENT_DEVICE_TYPE.value,
      });
    }
    setIsModalVisible(true);
  };

  // ✅ 新增自动携带【水表类型=2】；编辑与电表一致，可改 MAC、设备类型等
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingDevice) {
        const communicationType = values.communication_type !== undefined && values.communication_type !== '' && values.communication_type != null
          ? parseInt(values.communication_type, 10)
          : 1;
        const submitData = {
          ...values,
          device_status: parseInt(values.device_status),
          bind_room_id: values.bind_room_id ? parseInt(values.bind_room_id) : 0,
          electric_balance: parseFloat(values.electric_balance) || 0,
          electric_quantity: parseFloat(values.electric_quantity) || 0,
          min_recharge: parseFloat(values.min_recharge) || 0,
          management_fee: parseFloat(values.management_fee) || 0,
          communication_type: communicationType,
          device_detail_type: communicationType === 0
            ? (values.device_detail_type !== undefined && values.device_detail_type !== '' && values.device_detail_type != null ? parseInt(values.device_detail_type, 10) : 0)
            : null,
        };
        if (values.device_type !== undefined && values.device_type !== '' && values.device_type != null) {
          submitData.device_type = parseInt(values.device_type, 10);
        } else {
          delete submitData.device_type;
        }
        if (submitData.device_mac !== undefined && submitData.device_mac !== null) {
          submitData.device_mac = String(submitData.device_mac).trim() || null;
        }
        setLoading(true);
        const res = await updateDevice(editingDevice.id, submitData);
        res?.success ? message.success(`编辑${CURRENT_DEVICE_TYPE.label}成功`)
                    : message.error(`编辑${CURRENT_DEVICE_TYPE.label}失败：` + (res?.error || '后端异常'));
      } else {
        const targetDeviceType = values.device_type ? parseInt(values.device_type, 10) : CURRENT_DEVICE_TYPE.value;
        const submitData = {
          current_meter_no: values.current_meter_no,
          device_type: targetDeviceType,
          device_status: 1,
          device_mac: values.device_mac ? String(values.device_mac).trim() || undefined : undefined,
          bind_room_id: 0,
          min_recharge: 0,
          management_fee: 0,
          electric_balance: 0,
          electric_quantity: 0,
        };
        setLoading(true);
        const res = await addDevice(submitData);
        const deviceTypeName = DEVICE_TYPE_OPTIONS.find(opt => opt.value === targetDeviceType)?.label || '设备';
        res?.success ? message.success(`新增${deviceTypeName}成功`)
                    : message.error(`新增${deviceTypeName}失败：` + (res?.error || '后端异常'));
      }
      setIsModalVisible(false);
      loadDevices();
    } catch (error) {
      console.error('提交表单失败：', error);
      const errMsg = error?.response?.data?.error || error?.response?.data?.msg || error?.message || '提交失败，请检查输入内容';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

    // 分页变化处理
  const handleTableChange = (newPagination) => {
    setPagination({
          current: newPagination.current,
          pageSize: newPagination.pageSize,
          total: devices.length,
        });
    console.log('分页变化：', newPagination);
  };

  const pagedDevices = devices.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const handleDelete = async (id) => {
    if (!window.confirm(`确定要删除该${CURRENT_DEVICE_TYPE.label}吗？删除后不可恢复！`)) return;
    try {
      setLoading(true);
      await deleteDevice(id);
      message.success(`删除${CURRENT_DEVICE_TYPE.label}成功`);
      loadDevices();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('删除失败：', error);
      message.error(`删除${CURRENT_DEVICE_TYPE.label}失败，请稍后重试`);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(`请至少选择一个${CURRENT_DEVICE_TYPE.label}进行删除`);
      return;
    }
    if (!window.confirm(`确定要删除选中的${selectedRowKeys.length}个${CURRENT_DEVICE_TYPE.label}吗？删除后不可恢复！`)) return;
    try {
      setLoading(true);
      await batchDeleteDevices(selectedRowKeys);
      message.success(`成功删除${selectedRowKeys.length}个${CURRENT_DEVICE_TYPE.label}`);
      loadDevices();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('批量删除失败：', error);
      message.error(`批量删除${CURRENT_DEVICE_TYPE.label}失败，请稍后重试`);
    } finally {
      setLoading(false);
    }
  };

  const handleSupplementWater = (record) => {
    setSupplementDevice(record);
  };

  const markWaterSupplementNeedsRead = (deviceId) => {
    setWaterMeterNeedReadIds((prev) => new Set(prev).add(deviceId));
  };
  const clearWaterSupplementNeedsRead = (deviceId) => {
    setWaterMeterNeedReadIds((prev) => {
      const next = new Set(prev);
      next.delete(deviceId);
      return next;
    });
  };

  // 原始表格列配置
  const originalColumns = [
    {
      title: '操作',
      key: 'action',
      width: 340,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => showModal(record)}>编辑</Button>
          {isZtManufacturer(record) && (
            <Button
              type="primary"
              size="small"
              style={{
                backgroundColor: '#52c41a',
                borderColor: '#389e0d',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setPlatformSupplementDevice(record);
              }}
            >
              平台补水量
            </Button>
          )}
          {isYjWaterRemoteSupplementEligible(record) && (
            <Tooltip
              title={
                waterMeterNeedReadIds.has(record.id)
                  ? '已下发过补量，请先在弹窗内抄表后再补水'
                  : undefined
              }
            >
              <span>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto' }}
                  disabled={waterMeterNeedReadIds.has(record.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSupplementWater(record);
                  }}
                >
                  补水量
                </Button>
              </span>
            </Tooltip>
          )}
          {isYjWaterRemoteSupplementEligible(record) && (
            <>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', color: '#ff4d4f' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setMeteringFactorDevice(record);
                }}
              >
                计量系数
              </Button>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', color: '#1890ff' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setMeteringTotalDevice(record);
                }}
              >
                计量总额
              </Button>
            </>
          )}
        </Space>
      ),
    },
    { title: '水表ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: '设备MAC', dataIndex: 'device_mac', key: 'device_mac', width: 140 },
    { title: '厂商表号', dataIndex: 'factory_meter_no', key: 'factory_meter_no', width: 130 },
    { title: '当前表号', dataIndex: 'current_meter_no', key: 'current_meter_no', width: 130 },
    { title: 'IMEI', dataIndex: 'imei', key: 'imei', width: 160, render: (v) => (v ? String(v) : '-') },
    {
      title: '通讯类型',
      dataIndex: 'communication_type',
      key: 'communication_type',
      width: 100,
      render: (_, record) => {
        const t = Number(record?.communication_type);
        if (t === 0) return '4G';
        if (t === 1) return '蓝牙';
        if (t === 2) return 'WIFI';
        return '-';
      },
    },
    { title: '在线状态', dataIndex: 'is_online_name', key: 'is_online_name', width: 100 },
    {
      title: '设备详细类型',
      dataIndex: 'device_detail_type',
      key: 'device_detail_type',
      width: 120,
      render: (v, record) => (isDevice4GMeter(record) ? (Number(v) === 1 ? '4G主表' : '蓝牙副表') : ''),
    },
    {
      title: '设备厂家',
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      width: 140,
      render: (v) => {
        const code = String(v || '').trim();
        if (code === 'ZT') return '厂家一';
        return code || '-';
      },
    },
    { title: '设备状态', dataIndex: 'device_status_name', key: 'device_status_name', width: 90 },
    { title: '水表余额(元)', dataIndex: 'electric_balance', key: 'electric_balance', width: 100, render: (v) => (v || 0).toFixed(2) },
    { title: '剩余水量(吨)', dataIndex: 'electric_quantity', key: 'electric_quantity', width: 110, render: (v) => (v || 0).toFixed(2) },
    { title: '最低充值(元)', dataIndex: 'min_recharge', key: 'min_recharge', width: 100, render: (v) => (v || 0).toFixed(2) },
    { title: '管理费(元)', dataIndex: 'management_fee', key: 'management_fee', width: 100, render: (v) => (v || 0).toFixed(2) },
    {
      title: '总用水量(吨)',
      dataIndex: 'total_water_quantity',
      key: 'total_water_quantity',
      width: 110,
      render: (_, record) => Number(record.total_water_quantity ?? record.total_electricity ?? 0).toFixed(2),
    },
    {
      title: '水价(元/吨)',
      dataIndex: 'water_price',
      key: 'water_price',
      width: 100,
      render: (_, record) => Number(record.water_price ?? record.electric_price ?? 0).toFixed(2),
    },
    { title: '绑定房间', dataIndex: 'room_name', key: 'room_name', width: 100 },
    { title: '所属房源', dataIndex: 'house_name', key: 'house_name', width: 150 },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 160 },
    {
      title: '删除',
      key: 'delete_action',
      width: 88,
      render: (_, record) => (
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
          删除
        </Button>
      ),
    },
  ];

  let waterColumnStates = {};
  try {
    const raw = localStorage.getItem('waterMeterList_columnStates');
    if (raw) waterColumnStates = JSON.parse(raw);
  } catch (e) {
    console.error('Failed to parse column states:', e);
  }
  const waterResizableColumns = originalColumns
    .map((col) => (waterColumnStates[col.key] === 'hidden' ? null : col))
    .filter(Boolean);

  const rowSelection = { selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) };

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title={`${CURRENT_DEVICE_TYPE.label}管理`} />
      <ListToolbar
        showMatchMode
        matchMode={matchMode}
        onMatchModeChange={handleMatchModeChange}
        searchScopeOptions={DEVICE_LIST_SEARCH_SCOPE_OPTIONS}
        searchScope={searchScope}
        onSearchScopeChange={handleSearchScopeChange}
        showLocationSearch
        locationPlaceholder={getDeviceLocationSearchPlaceholder(locationScope)}
        locationValue={locationText}
        onLocationChange={setLocationText}
        locationScope={locationScope}
        onLocationScopeChange={handleLocationScopeChange}
        locationMatchMode={locationMatchMode}
        onLocationMatchModeChange={handleLocationMatchModeChange}
        searchPlaceholder={getDeviceListSearchPlaceholder(searchScope)}
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        onExport={handleExport}
        showBatchDelete={true}
        selectedCount={selectedRowKeys.length}
        onBatchDelete={handleBatchDelete}
        showAdd={true}
        onAdd={() => showModal()}
        actions={[
          {
            key: 'download-template',
            label: '下载模板',
            icon: <DownloadOutlined />,
            onClick: handleDownloadTemplate,
            type: 'default',
          },
          {
            key: 'batch-import',
            label: '批量导入',
            icon: <UploadOutlined />,
            onClick: handleBatchImport,
            type: 'default',
          },
        ]}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <ResizableTable
        columns={waterResizableColumns.length > 0 ? waterResizableColumns : originalColumns}
        dataSource={pagedDevices}
        loading={loading}
        rowKey="id"
        storageKey="waterMeterList"
        rowSelection={rowSelection}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{ x: 2000 }}
        onChange={handleTableChange}
        locale={{ emptyText: <Empty description={`暂无${CURRENT_DEVICE_TYPE.label}数据`} /> }}
      />
      <Modal
        title={editingDevice ? `编辑${CURRENT_DEVICE_TYPE.label}` : '新增设备'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
        width={600}
        destroyOnClose
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          {editingDevice ? (
            <>
              <Form.Item
                name="device_type"
                label="设备类型"
                rules={[{ required: true, message: '请选择设备类型' }]}
              >
                <Select placeholder="请选择设备类型" options={DEVICE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="device_mac"
                label="设备MAC"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="选填" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="factory_meter_no"
                label="厂商表号"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入厂商表号" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="current_meter_no"
                label="当前表号"
                rules={[{ required: true, message: '请输入当前表号' }, { max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入当前表号" maxLength={50} />
              </Form.Item>
              <Form.Item name="communication_type" label="通讯类型">
                <Select
                  placeholder="不填默认蓝牙"
                  allowClear
                  options={[
                    { value: 0, label: '4G' },
                    { value: 1, label: '蓝牙' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="device_detail_type" label="设备详细类型">
                <Select
                  placeholder={Number(watchedCommunicationType) === 0 ? '不填默认蓝牙副表' : '蓝牙设备无详细类型'}
                  allowClear
                  disabled={Number(watchedCommunicationType) !== 0}
                  options={[
                    { value: 0, label: '蓝牙副表' },
                    { value: 1, label: '4G主表' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="device_status"
                label="设备状态"
                rules={[{ required: true, message: '请选择设备状态' }]}
              >
                <Select placeholder="请选择设备状态" options={DEVICE_STATUS_OPTIONS} />
              </Form.Item>
              <Form.Item name="bind_room_id" label="绑定房间">
                <Select
                  placeholder="先输入关键词再选房间（房源名/房号）"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) => {
                    const q = String(input ?? '').trim().toLowerCase();
                    if (!q) return false;
                    return String(option?.label ?? '')
                      .toLowerCase()
                      .includes(q);
                  }}
                >
                  {rooms.map((room) => {
                    const label =
                      (room.houseName ?? room.house_name ?? '-') +
                      ' - ' +
                      (room.number ?? room.fang_hao ?? '-');
                    return (
                      <Option key={room.id} value={room.id.toString()} label={label}>
                        {label}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item
                  name="electric_balance"
                  label="水表余额（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
                <Form.Item
                  name="electric_quantity"
                  label="剩余水量（吨）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效水量（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
              </Space.Compact>
              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item
                  name="min_recharge"
                  label="最低充值（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
                <Form.Item
                  name="management_fee"
                  label="管理费（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
              </Space.Compact>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  label="总用水量（吨）"
                  initialValue={Number(editingDevice.total_water_quantity ?? editingDevice.total_electricity ?? 0).toFixed(2)}
                  style={{ flex: 1 }}
                >
                  <Input disabled />
                </Form.Item>
                <Form.Item
                  label="水价（元/吨）"
                  initialValue={Number(editingDevice.water_price ?? editingDevice.electric_price ?? 0).toFixed(2)}
                  style={{ flex: 1 }}
                >
                  <Input disabled />
                </Form.Item>
              </Space.Compact>
            </>
          ) : (
            <>
              <Form.Item
                name="device_type"
                label="设备类型"
                rules={[{ required: true, message: '请选择设备类型' }]}
              >
                <Select placeholder="请选择设备类型" options={DEVICE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="current_meter_no"
                label="当前表号"
                rules={[{ required: true, message: '请输入当前表号' }, { max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入当前表号（必填）" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="device_mac"
                label="设备MAC"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="选填" maxLength={50} />
              </Form.Item>
              <div style={{ color: '#999', fontSize: '12px', marginTop: '-16px', marginBottom: '16px' }}>
                其他字段将使用默认值：设备状态=在线/正常，余额=0，未绑定房间
              </div>
            </>
          )}
        </Form>
      </Modal>

      <MeterSupplementModal
        open={!!supplementDevice}
        device={
          supplementDevice
            ? devices.find((d) => d.id === supplementDevice.id) || supplementDevice
            : null
        }
        variant="water"
        supplementBlockedUntilRead={
          supplementDevice ? waterMeterNeedReadIds.has(supplementDevice.id) : false
        }
        onSupplementIssued={markWaterSupplementNeedsRead}
        onMeterReadSuccess={clearWaterSupplementNeedsRead}
        onClose={() => setSupplementDevice(null)}
        onRefresh={loadDevices}
      />

      <MeterMeteringFactorModal
        open={!!meteringFactorDevice}
        device={
          meteringFactorDevice
            ? devices.find((d) => d.id === meteringFactorDevice.id) || meteringFactorDevice
            : null
        }
        variant="water"
        onClose={() => setMeteringFactorDevice(null)}
      />

      <MeterMeteringTotalModal
        open={!!meteringTotalDevice}
        device={
          meteringTotalDevice
            ? devices.find((d) => d.id === meteringTotalDevice.id) || meteringTotalDevice
            : null
        }
        variant="water"
        onClose={() => setMeteringTotalDevice(null)}
      />

      <Modal
        title="平台补水量"
        open={!!platformSupplementDevice}
        onCancel={() => setPlatformSupplementDevice(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setPlatformSupplementDevice(null)}>
            关闭
          </Button>,
        ]}
        destroyOnClose
        maskClosable
      >
        <Text type="secondary">功能开发中…</Text>
      </Modal>
    </div>
  );
};
export default WaterMeterList;